import { IntlShape } from 'react-intl';
import { ButtonProps } from '@designsystem/stories/02-atoms/Button';
import { InfoItemProps } from '@designsystem/stories/02-atoms/Info';
import { PersonContent, TypeNormalizer, CardStyle } from '../types';
import { stringOrUndefined } from '../utils';
import { normalizeContent, normalizeLink } from '../normalizeContent';
import { messages } from '../messages';

/**
 * Normalizes person-type content (Contact, TeamContact)
 */
export const normalizePersonCard: TypeNormalizer = (item, normalized, intl) => {
  const personItem = item as PersonContent;
  const style: CardStyle = normalized?.style;

  // Add name prefix if available
  if (personItem?.name_affix1) {
    normalized.title = personItem.name_affix1 + ' ' + normalized.title;
  }

  // Set role as description
  if (personItem?.role) {
    const roleContent = Array.isArray(personItem.role)
      ? personItem.role
      : [personItem.role];
    normalized.description = stringOrUndefined(roleContent, intl);
  }

  // Build contact information
  const info = buildContactInfo(personItem, intl, style);
  if (info.length > 0) {
    normalized.info = info;
  }

  // Build action buttons
  const actions = buildPersonActions(personItem, intl, style);
  if (actions.length > 0) {
    normalized.actions = actions;
  }

  // Set placeholder image if no image provided
  if (!normalized?.image) {
    normalized.image = createPersonPlaceholder(personItem);
  } else {
    normalized.image.aspect = 'landscape';
  }

  return normalized;
};

/**
 * Builds contact information section for person cards
 */
const buildContactInfo = (
  item: PersonContent,
  intl: IntlShape,
  style?: CardStyle,
): InfoItemProps[] => {
  const info: InfoItemProps[] = [];

  if (style === 'tile') {
    return info;
  }

  if (item?.phone1 || item?.phone2) {
    info.push({
      key: intl.formatMessage(messages.Phone),
      value: (
        <>
          {item?.phone1 && (
            <>
              <a href={'tel:' + item.phone1}>{item.phone1}</a>
              {item?.phone1_suffix &&
                ' (' + normalizeContent(item.phone1_suffix, true) + ')'}
            </>
          )}
          {item?.phone1 && item?.phone2 && <br />}
          {item?.phone2 && (
            <>
              <a href={'tel:' + item.phone2}>{item.phone2}</a>
              {item?.phone2_suffix &&
                ' (' + normalizeContent(item.phone2_suffix, true) + ')'}
            </>
          )}
        </>
      ),
    });
  }

  // Email
  if (item?.email) {
    info.push({
      key: intl.formatMessage(messages.Email),
      value: <a href={'mailto:' + item.email}>{item.email}</a>,
    });
  }

  return info;
};

/**
 * Builds action buttons for person cards
 */
const buildPersonActions = (
  item: PersonContent,
  intl: IntlShape,
  style?: CardStyle,
): ButtonProps[] => {
  const actions: ButtonProps[] = [];

  if (style === 'tile') {
    return actions;
  }

  // Booking/Meeting button
  if (item?.booking_url) {
    actions.push({
      type: 'primary',
      size: 'small',
      title: intl.formatMessage(messages.ScheduleMeeting),
      icon: 'calender',
      href: normalizeLink(item.booking_url),
      target: '_blank',
    });
  }

  // Map button
  if (item?.google_maps_url) {
    actions.push({
      type: 'secondary',
      size: 'small',
      title: intl.formatMessage(messages.ShowOnMap),
      icon: 'map',
      href: normalizeLink(item.google_maps_url),
      target: '_blank',
    });
  }

  return actions;
};

/**
 * Creates placeholder image for person cards
 */
const createPersonPlaceholder = (item: PersonContent) => {
  return {
    className: 'media--placeholder',
    aspect: 'landscape' as const,
    contain: true,
    children: (
      <svg
        className="media__object"
        xmlns="http://www.w3.org/2000/svg"
        width="85"
        height="50"
        viewBox="732 279 85 50"
      >
        <g fill="#1A1918" fill-rule="nonzero">
          <path d="M767.4 328.6h1.7v-48.9h-1.7z" />
          <path d="M742 295.9c.8-1.1 2.6-4.6 8-4.6 3.8 0 5.8 1.5 6.6 2.2 2.3 2.2 2.5 5.5 2.5 7.3v15.5h-9.7v-13.6c0-.3 0-3.5-3.3-3.5-3.4 0-3.5 3.1-3.5 4v13.1h-9.8v-24.4h9.2v4" />
          <path d="M812.2 291.4c-1.8 0-3.3 1.5-3.3 3.3 0 1.8 1.5 3.3 3.3 3.3 1.7 0 2.4-1.2 2.4-1.1.9 2.8-4.1 17.6-10.9 17.6-2.2 0-2.9-1.8-2.9-4.1 0-2.7.8-5.5 1.8-9.2 1.1-3.7 2-7.2 2.5-9.1h-6.6c-.9 3.2-7.3 22-12.8 22-.7 0-1.4-.7-1.4-2 0-.7.6-3.3 1-4.2l2.2-7.3c.5-1.5 1.2-3.5 1.2-5 0-2.6-1.8-4-3.9-4-6 0-8.4 5.3-9.7 7.4l1 .6c.6-.9 3.2-5.4 5-5.4.3 0 .5.3.5 1.2 0 .6-.4 2-.7 3.3l-2.4 7.3c-.4 1.4-1.3 4-1.3 5.8 0 3.4 2.6 5.2 5 5.2 8.5 0 12.1-10.1 13.3-13l.1.1c-.2.6-.9 3.6-.9 5.2 0 4.5 2 7.7 6.5 7.7 12.8 0 19.8-25.6 11-25.6" />
        </g>
      </svg>
    ),
  };
};
